/**
 * Subscriptions (PRD v3.2 section 2, day 35): the plan the student is on, the
 * three plans, the order, and Credits on their own.
 *
 *   the strip      one line: the plan, its Credits, when it ends, and the
 *                  Standing Lawyers sponsorship when the student holds one
 *   the plans      Base, Premium and Elite, each a year, in US dollars, with
 *                  its Credits; nothing else written on them
 *   the order      opens under the plans once one is chosen: the server's
 *                  quote, in dollars, with the sponsorship taken off Elite,
 *                  and the rupees charged at the day's rate
 *   Credits        $1.50 each, 5 for $7.50; bought from day 61
 *
 * The browser never names a price it charges. The cards print the catalogue's
 * (the same data the server prices by); the order prints the server's quote,
 * and Pay opens the order the server priced. A sponsorship is applied by the
 * server, never sent from here as a code.
 *
 * Until Razorpay is live (day 84) the quote says payments are not open, and
 * the order says so in place of taking anything. In development the
 * development provider confirms the order at once, so the whole flow runs.
 */

import { useEffect, useState } from "preact/hooks";
import type { CheckoutErrorCode, QuoteResponse } from "@ls/shared";
import { CREDIT_TOPUP_PACK, TIERS, TIER_CREDITS, formatDollars, tierOf, type TierId } from "@ls/shared/catalogue";
import { ApiUnavailable, expect, http, type ApiReply } from "../api";
import { HELP_EMAIL, WORDMARK } from "../brand";
import {
  accessErrorContract,
  checkoutErrorContract,
  checkoutResponseContract,
  myVoucherResponseContract,
  quoteResponseContract,
} from "../contracts.generated";
import { useSession } from "../shell/context";
import type { PageProps } from "../shell/routes";
import { planLine } from "../shell/Sidebar";
import { Button, Checkout, PageHeader, PlanCard } from "../ui";

const NAME = `${WORDMARK.lead}${WORDMARK.tail}`;

export function longDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

export function rupees(paise: number): string {
  return `₹${(paise / 100).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

/** The sponsorship's words on the strip. */
export function sponsorshipLine(expiresAt: string): string {
  return `Standing Lawyers sponsors $88 of your year of Elite, until ${longDate(expiresAt)}`;
}

/** What a refusal means to the student, or null when the page should ask the API again instead. */
export function checkoutWording(code: CheckoutErrorCode): string | null {
  switch (code) {
    case "payments_unavailable":
      return "Payments open soon.";
    case "checkout_not_found":
    case "checkout_expired":
      return "That payment was not completed. Try again.";
    case "rate_limited":
      return "Too many attempts. Try again in a few minutes.";
    case "voucher_expired":
    case "voucher_voided":
      return `Your sponsorship could not be applied. Write to ${HELP_EMAIL} and we will look at it.`;
    case "server_misconfigured":
      return "This is unavailable right now. Try again in a few minutes.";
    case "invalid_request":
    case "voucher_not_found":
    case "voucher_already_used":
    case "voucher_not_yours":
    case "voucher_not_for_tier":
      return "Something went wrong. Try again.";
    case "unauthenticated":
    case "already_on_a_plan":
      return null;
  }
}

export function Subscriptions(_: PageProps) {
  const session = useSession();
  const { plan } = session;
  /** A year bought and running: there is nothing more to sell until it ends. */
  const yearRunning = plan.type === "annual";
  const [sponsoredUntil, setSponsoredUntil] = useState<string | null>(null);
  const [chosen, setChosen] = useState<TierId | null>(null);
  const [quote, setQuote] = useState<QuoteResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The sponsorship, read once: only a nicety on the strip, since the server applies it whatever the strip says.
  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const reply = await http.get("/vouchers/mine");
        const parsed = reply.status === 200 ? myVoucherResponseContract.safeParse(reply.body) : null;
        const voucher = parsed?.success ? parsed.data.voucher : null;
        if (live && voucher?.state === "live") setSponsoredUntil(voucher.expiresAt);
      } catch {
        // The strip shows the plan alone.
      }
    })();
    return () => {
      live = false;
    };
  }, [plan.type, plan.tier]);

  /** A refusal: the sign in or the plan changed underneath, so ask again; or words for the student. */
  const refused = (reply: ApiReply, path: string) => {
    const access = accessErrorContract.safeParse(reply.body);
    if (access.success && access.data.error !== "server_misconfigured") {
      void session.refresh();
      return;
    }
    const problem = checkoutErrorContract.safeParse(reply.body);
    if (!problem.success) throw new ApiUnavailable(`${path} answered ${reply.status} outside the contract.`);
    const said = checkoutWording(problem.data.error);
    if (said === null) {
      setChosen(null);
      void session.refresh();
      return;
    }
    setError(said);
  };

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (caught) {
      setError(caught instanceof ApiUnavailable ? `${NAME} could not be reached. Check your connection.` : "Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const choose = (tier: TierId) => {
    setChosen(tier);
    setQuote(null);
    void run(async () => {
      const reply = await http.post("/checkout/quote", { tier, autoApplySponsorship: true });
      if (reply.status === 200) setQuote(expect(reply, quoteResponseContract, "/checkout/quote"));
      else refused(reply, "/checkout/quote");
    });
  };

  const pay = () =>
    run(async () => {
      if (!chosen) return;
      const opened = await http.post("/checkout", { tier: chosen, autoApplySponsorship: true });
      if (opened.status !== 201) {
        refused(opened, "/checkout");
        return;
      }
      const order = expect(opened, checkoutResponseContract, "/checkout");
      if (order.status === "open") {
        // The development provider stands in for the gateway; Razorpay's checkout arrives on day 84.
        const done = await http.post(`/checkout/${encodeURIComponent(order.checkoutId)}/complete`, {});
        if (done.status !== 200) {
          refused(done, "/checkout/complete");
          return;
        }
      }
      setChosen(null);
      setQuote(null);
      await session.refresh();
    });

  const quoted = chosen && quote?.tier === chosen ? quote : null;
  const strip = [planLine(plan), plan.expiresAt ? `until ${longDate(plan.expiresAt)}` : null].filter(Boolean).join(" · ");

  return (
    <div class="feature subs">
      <PageHeader title="Subscriptions" />
      <p class="subs-strip" role="status" data-subs-strip>
        <b>{strip}</b>
        {sponsoredUntil && !yearRunning ? <span data-sponsorship>{sponsorshipLine(sponsoredUntil)}</span> : null}
      </p>
      <div class="plans-grid" data-plans>
        {TIERS.map((tier) => (
          <PlanCard
            key={tier.id}
            name={tier.name}
            price={formatDollars(tier.yearDollars)}
            period="a year"
            credits={`${TIER_CREDITS[tier.id]} Credits`}
            current={yearRunning && plan.tier === tier.id}
            featured={chosen === tier.id}
            action={
              yearRunning ? null : (
                <Button variant={chosen === tier.id ? "primary" : "secondary"} disabled={busy} onClick={() => choose(tier.id)}>
                  Choose {tier.name}
                </Button>
              )
            }
          />
        ))}
      </div>
      {error ? (
        <p class="notice bad subs-error" role="alert">
          {error}
        </p>
      ) : null}
      {quoted ? (
        <div data-order>
          <Checkout
            title="Order summary"
            lines={[
              { label: `${tierOf(quoted.tier).name}, one year, ${TIER_CREDITS[quoted.tier]} Credits`, amount: formatDollars(quoted.listDollars ?? tierOf(quoted.tier).yearDollars) },
              ...(quoted.discountDollars ? [{ label: "Paid by the law firm", info: "Standing Lawyers · Merit fellowship granted in recognition of your internship service", amount: `−${formatDollars(quoted.discountDollars)}`, credit: true }] : []),
            ]}
            total={{ label: "Total", amount: formatDollars(quoted.amountDollars ?? tierOf(quoted.tier).yearDollars) }}
            note={`Charged as ${rupees(quoted.amountPaise)}, at ₹${(quoted.fxRate ?? 85).toFixed(2)} to the dollar.${quoted.paymentsOpen ? "" : " Payments open soon."}`}
            action={
              <Button variant="primary" block disabled={busy || !quoted.paymentsOpen} onClick={() => void pay()}>
                {busy ? "Working..." : `Pay ${rupees(quoted.amountPaise)}`}
              </Button>
            }
          />
        </div>
      ) : chosen && busy ? (
        <p class="note" role="status">
          Pricing {tierOf(chosen).name}...
        </p>
      ) : null}
      <section class="subs-topup" aria-label="Credits" data-topup>
        <b>Credits</b>
        <span>
          {formatDollars(CREDIT_TOPUP_PACK.unitDollars)} each, or {CREDIT_TOPUP_PACK.packCredits} for {formatDollars(CREDIT_TOPUP_PACK.packDollars)}
        </span>
        <span class="muted">Opens soon</span>
      </section>
    </div>
  );
}
